import { isLargeUp } from "utils/breakpoints"

const OVERLAY_AD_SELECTORS = ["#adb-adhesion-container-1", "#inScreen"]

function setOverlayAdsVisible(visible: boolean): void {
  Array.from(document.querySelectorAll<HTMLElement>(OVERLAY_AD_SELECTORS.join(","))).forEach(
    (element) => {
      const ad = element
      ad.style.display = visible ? "" : "none"
    },
  )
}

function scrollToTop(): Promise<void> {
  const startY = window.scrollY
  if (startY === 0 || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    window.scrollTo(0, 0)
    return Promise.resolve()
  }

  return new Promise((resolve) => {
    const startTime = performance.now()

    const step = (now: number) => {
      const progress = Math.min(1, (now - startTime) / 50)
      const eased = 1 - (1 - progress) ** 3
      window.scrollTo({ top: startY * (1 - eased), left: 0, behavior: "auto" })

      if (progress < 1) {
        window.requestAnimationFrame(step)
      } else {
        window.scrollTo(0, 0)
        resolve()
      }
    }

    window.requestAnimationFrame(step)
  })
}

export class MobileMenu {
  private navHeader = document.querySelector<HTMLElement>(".page-header__nav-header")
  private menuBtn = document.querySelector<HTMLElement>("#mobile-menu")
  private menuNav = document.querySelector<HTMLElement>(".menu-nav")
  private logoContainer = document.querySelector<HTMLElement>(".page-header__logo-container")
  private searchBtn = document.querySelector<HTMLElement>(
    ".location-nav-header-text-field__mob-search-button",
  )
  private searchField = document.querySelector<HTMLElement>(".location-nav-header-text-field")
  private searchForm = document.querySelector<HTMLElement>(
    ".location-nav-header-text-field__search-form",
  )
  private searchInput = document.querySelector<HTMLElement>(
    ".location-nav-header-text-field .location-search__input",
  )
  private isOpening = false

  constructor() {
    this.addEventListeners()
    this.closeSearch()
  }

  private addEventListeners() {
    this.menuBtn?.addEventListener("click", (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.toggle()
    })

    this.searchBtn?.addEventListener("click", (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.openSearch()
    })

    document.addEventListener("click", (e) => {
      const target = e.target as Node

      if (this.isOpen() && !this.menuNav?.contains(target) && !this.menuBtn?.contains(target)) {
        this.close()
      }

      if (this.isSearchOpen() && !this.searchField?.contains(target)) {
        this.closeSearch()
      }
    })

    window.addEventListener("resize", () => {
      if (isLargeUp()) {
        this.close()
        if (this.isSearchOpen()) this.closeSearch()
      }
    })
  }

  private isOpen(): boolean {
    return !!this.menuNav?.classList.contains("active")
  }

  private toggle(): void {
    if (this.isOpen() || this.isOpening) {
      this.close()
    } else {
      this.open()
    }
  }

  private async open(): Promise<void> {
    if (!this.menuNav || !this.menuBtn) return

    this.closeSearch()
    this.isOpening = true
    await scrollToTop()

    if (!this.isOpening || isLargeUp() || !this.menuNav || !this.menuBtn) return

    this.isOpening = false
    this.menuNav.classList.add("active")
    this.menuBtn.classList.add("active")
    this.menuBtn.setAttribute("aria-expanded", "true")
    this.navHeader?.classList.add("mm-active")
    document.addEventListener("touchmove", this.preventBackgroundScroll, { passive: false })
    setOverlayAdsVisible(false)
  }

  private close(): void {
    this.isOpening = false
    if (!this.isOpen()) return

    this.menuNav?.classList.remove("active")
    this.menuBtn?.classList.remove("active")
    this.menuBtn?.setAttribute("aria-expanded", "false")
    this.navHeader?.classList.remove("mm-active")
    document.removeEventListener("touchmove", this.preventBackgroundScroll)
    setOverlayAdsVisible(true)
  }

  private isSearchOpen(): boolean {
    return !!this.searchField?.classList.contains("search-active")
  }

  private openSearch(): void {
    this.close()
    this.searchField?.classList.add("search-active")
    this.logoContainer?.classList.add("search-active")
    this.searchBtn?.classList.add("active")
    this.searchBtn?.setAttribute("aria-expanded", "true")
    this.searchForm?.setAttribute("aria-hidden", "false")
    this.searchInput?.focus()
  }

  private closeSearch(): void {
    if (!this.searchField) return

    this.searchField?.classList.remove("search-active")
    this.logoContainer?.classList.remove("search-active")
    this.searchBtn?.classList.remove("active")
    this.searchBtn?.setAttribute("aria-expanded", "false")
    this.searchForm?.setAttribute("aria-hidden", isLargeUp() ? "false" : "true")
  }

  private preventBackgroundScroll = (event: TouchEvent): void => {
    const scroller = this.menuNav?.querySelector(".menu-nav__menu-container")
    if (scroller?.contains(event.target as Node)) return
    event.preventDefault()
  }
}
